import { APIKit } from "config/axios";

import { RouteConstants, ServerConstants } from "constant";
import { UserDataOptionsResponse, UserDataRequest, UserDataResponse, UserResponse } from "types";
import { AppUtils } from "utils";

import * as PublicService from "./public";

const { ROUTE_LOGIN } = RouteConstants;
const { API_V1_USER_DATA, API_V1_USERS } = ServerConstants;

async function retryWithRenewal<T>(request: () => Promise<T>, retryOnUnauthorized = true): Promise<T> {
  try {
    return await request();
  } catch (err: any) {
    const errStatus = JSON.parse(JSON.stringify(err))?.status ?? err?.response?.status;

    if (errStatus === 401 && retryOnUnauthorized) {
      const renewed = await PublicService.renew();

      if (renewed) {
        return retryWithRenewal(request, false);
      }

      AppUtils.logout();
      if (window.location.pathname !== ROUTE_LOGIN) {
        window.location.href = ROUTE_LOGIN;
      }
    }

    throw err;
  }
}

function normalizeRequestPayload(data: UserDataRequest): UserDataRequest {
  return {
    ...data,
    languages: data.languages ?? [],
  };
}

function normalizeErrorField(value: unknown): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function isUserDataGetRequest(err: any): boolean {
  const responseConfig = err?.response?.config ?? err?.config;
  const method = normalizeErrorField(responseConfig?.method);
  const url = String(responseConfig?.url ?? "").split("?")[0];

  if (method && method !== "get") {
    return false;
  }

  if (url.endsWith(`${API_V1_USER_DATA}/options`)) {
    return false;
  }

  return (
    url.endsWith(API_V1_USER_DATA) ||
    new RegExp(`${API_V1_USER_DATA}/\\d+$`).test(url)
  );
}

export function isUserDataNotFoundError(err: any): boolean {
  const status = JSON.parse(JSON.stringify(err))?.status ?? err?.response?.status;
  const responseData = err?.response?.data ?? err?.data;
  const code = normalizeErrorField(responseData?.code);
  const message = normalizeErrorField(responseData?.message);
  const description = normalizeErrorField(responseData?.description);
  const knownNotFoundValues = [
    "user data not found",
    "user_data_not_found",
  ];

  // Swagger documents `400 User data not found` for the missing-record case.
  // Treat only that known backend response as an empty profile state.
  if (status !== 400) {
    return false;
  }

  const matchesKnownResponse = knownNotFoundValues.some(
    (value) =>
      code === value ||
      message === value ||
      description === value,
  );

  if (matchesKnownResponse) {
    return true;
  }

  // Error payloads can arrive encrypted, so fall back to the documented
  // `400 User data not found` contract for the two user-data GET endpoints.
  return isUserDataGetRequest(err);
}

export async function getMyUserData(): Promise<UserDataResponse> {
  return retryWithRenewal(async () => {
    const response = await APIKit.get(API_V1_USER_DATA);
    return response.data as UserDataResponse;
  });
}

export async function createMyUserData(data: UserDataRequest): Promise<UserDataResponse> {
  return retryWithRenewal(async () => {
    const response = await APIKit.post(API_V1_USER_DATA, normalizeRequestPayload(data));
    return response.data as UserDataResponse;
  });
}

export async function updateMyUserData(data: UserDataRequest): Promise<UserDataResponse> {
  return retryWithRenewal(async () => {
    const response = await APIKit.put(API_V1_USER_DATA, normalizeRequestPayload(data));
    return response.data as UserDataResponse;
  });
}

export async function getUserData(userId: number): Promise<UserDataResponse> {
  return retryWithRenewal(async () => {
    const response = await APIKit.get(`${API_V1_USER_DATA}/${userId}`);
    return response.data as UserDataResponse;
  });
}

export async function createUserData(
  userId: number,
  data: UserDataRequest,
): Promise<UserDataResponse> {
  return retryWithRenewal(async () => {
    const response = await APIKit.post(
      `${API_V1_USER_DATA}/${userId}`,
      normalizeRequestPayload(data),
    );
    return response.data as UserDataResponse;
  });
}

export async function updateUserData(
  userId: number,
  data: UserDataRequest,
): Promise<UserDataResponse> {
  return retryWithRenewal(async () => {
    const response = await APIKit.put(
      `${API_V1_USER_DATA}/${userId}`,
      normalizeRequestPayload(data),
    );
    return response.data as UserDataResponse;
  });
}

export async function getUserDataOptions(): Promise<UserDataOptionsResponse> {
  return retryWithRenewal(async () => {
    const response = await APIKit.get(`${API_V1_USER_DATA}/options`);
    return response.data as UserDataOptionsResponse;
  });
}

export async function updateUserWorkName(
  user: UserResponse,
  firstName: string,
  lastName: string,
): Promise<UserResponse> {
  return retryWithRenewal(async () => {
    // PATCH /users/{id} requires the full user payload; teamLeadId is omitted to keep it unchanged.
    const response = await APIKit.patch(`${API_V1_USERS}/${user.id}`, {
      firstName,
      lastName,
      countryCode: user.countryCode,
      phoneNumber: user.phoneNumber,
      profileId: user.profile.id,
      status: user.status,
    });
    return response.data as UserResponse;
  });
}
